import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { formatTimestamp } from "../lib/formatTime.js";
import type {
  Notification,
  NotificationListResponse
} from "../lib/notifications.js";

/**
 * Notification bell + drawer (ADR-0044).
 * Spec: governance-foundation §13.1 / §13.2.
 *
 * Bell lives in the tenant topbar. Polls `unread-count` on mount and on
 * window focus; badge hidden when zero. Clicking opens the drawer.
 *
 * Drawer:
 *   - On open: lists active notifications + fires POST /mark-all-read exactly once.
 *   - Per row: dismiss (× button) moves the row to trash.
 *   - Footer links navigate to /notifications/trash and /settings/notifications.
 *
 * Bell and drawer ship together in this file because their state is tightly
 * coupled (open/close + badge refresh after drawer close).
 */

function IconBell({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

const UNREAD_POLL_INTERVAL_MS = 60_000;

export function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const inFlightRef = useRef(false);

  const refreshUnread = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await apiFetch<{ count: number }>("/api/tenant/notifications/unread-count");
      setUnreadCount(typeof res.count === "number" ? res.count : 0);
    } catch {
      // Soft-fail — next focus/poll tick will try again.
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    void refreshUnread();
    const interval = window.setInterval(() => void refreshUnread(), UNREAD_POLL_INTERVAL_MS);
    const onFocus = () => void refreshUnread();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshUnread]);

  function openDrawer() { setDrawerOpen(true); }
  function closeDrawer() {
    setDrawerOpen(false);
    void refreshUnread();
  }

  return (
    <>
      <button
        type="button"
        className="menuButton"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        onClick={openDrawer}
        style={{ position: "relative" }}
      >
        <IconBell size={18} />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              borderRadius: 999,
              background: "var(--danger-text)",
              color: "white",
              textAlign: "center"
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {drawerOpen && <NotificationDrawer onClose={closeDrawer} />}
    </>
  );
}

/**
 * Resolve the click-through target for a notification row. Entity types
 * with a known in-app route deep-link directly (e.g. action_item →
 * `/app/action-items/{id}`). Everything else falls back to the notification's
 * own detail page (`/app/notifications/{id}`) so every row is clickable —
 * a user should never click a notification and have nothing happen. Extend
 * the switch when new entity types ship.
 */
function linkForNotification(n: Pick<Notification, "id" | "entity_type" | "entity_id">): string {
  if (n.entity_type && n.entity_id) {
    switch (n.entity_type) {
      case "action_item":
        return `/app/action-items/${n.entity_id}`;
      // Fall through to detail page for unknown entity types.
    }
  }
  return `/app/notifications/${n.id}`;
}

function NotificationDrawer({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The drawer shows UNREAD notifications only. Clicking X marks one as
  // read; the "Mark all as read" footer button empties the drawer in bulk.
  // Neither moves rows to trash — that's a deliberate action from the full
  // Inbox page.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiFetch<NotificationListResponse>("/api/tenant/notifications?status=unread&limit=50");
      setItems(list.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function clearOne(id: string) {
    const prev = items;
    setItems((rows) => rows.filter((r) => r.id !== id));
    try {
      await apiFetch(`/api/tenant/notifications/${id}`, {
        method: "PATCH",
        body: { status: "read" }
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setItems(prev);
    }
  }

  async function markAllRead() {
    setBusy(true);
    try {
      await apiFetch("/api/tenant/notifications/mark-all-read", { method: "POST" });
      setItems([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="notificationDrawerOverlay"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="notificationDrawer"
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        onClick={(e) => e.stopPropagation()}
      >
        <header style={{ padding: 16, borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700 }}>Notifications</div>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
          {loading && <div className="muted" style={{ padding: 16 }}>Loading…</div>}
          {error && <div className="help" style={{ color: "var(--danger-text)", padding: 16 }}>{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="muted" style={{ padding: 16 }}>You're all caught up.</div>
          )}
          {items.map((n) => <NotificationRow key={n.id} n={n} onClear={() => void clearOne(n.id)} onNavigate={onClose} />)}
        </div>

        <footer style={{ padding: 12, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/app/notifications" onClick={onClose} className="muted" style={{ fontSize: 13 }}>Inbox</Link>
          <button
            type="button"
            className="btn btnSm"
            onClick={() => void markAllRead()}
            disabled={busy || items.length === 0}
          >
            Mark all as read
          </button>
          <Link to="/app/settings/notifications" onClick={onClose} className="muted" style={{ fontSize: 13 }}>Preferences</Link>
        </footer>
      </aside>
    </div>
  );
}

function NotificationRow({
  n,
  onClear,
  onNavigate
}: {
  n: Notification;
  onClear: () => void;
  onNavigate: () => void;
}) {
  const categoryPill = categoryPillClass(n.category);
  const to = linkForNotification(n);

  const baseStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "10px 12px",
    borderBottom: "1px solid var(--border)",
    background: "var(--surface-2, rgba(0,0,0,0.03))",
    color: "inherit",
    textDecoration: "none",
    cursor: "pointer"
  };

  return (
    <Link to={to} onClick={onNavigate} style={baseStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{n.title}</div>
        <button
          type="button"
          aria-label="Clear"
          title="Clear this notification"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClear(); }}
          className="btn btnSm"
          style={{ padding: "0 6px", lineHeight: "20px", minWidth: 24 }}
        >
          ×
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{truncate(previewFor(n), 240)}</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className={`pill ${categoryPill}`} style={{ fontSize: 10 }}>{n.category}</span>
        <span className="muted" style={{ fontSize: 11 }}>{formatTimestamp(n.created_at)}</span>
      </div>
    </Link>
  );
}

function categoryPillClass(category: string): string {
  switch (category) {
    case "system": return "pillWarn";
    case "digest": return "pillNeutral";
    default: return "pillNeutral";
  }
}

/**
 * What the drawer shows under the title. A collapsed event's `body` is the
 * email's text — bare URLs and all — so the preview lists row titles instead
 * of showing the reader a wall of links they cannot click here.
 */
function previewFor(n: Notification): string {
  if (!n.lines || n.lines.length === 0) return n.body;
  const titles = n.lines.map((l) => l.title).join(" · ");
  const omitted = Math.max(0, (n.total_count ?? n.lines.length) - n.lines.length);
  return omitted > 0 ? `${titles} · …and ${omitted} more` : titles;
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

